(() => {
  'use strict';
  const card = document.querySelector('[data-frequency-challenge]');
  if (!card) return;
  const start = card.querySelector('[data-frequency-start]');
  const stop = card.querySelector('[data-frequency-stop]');
  const status = card.querySelector('[data-frequency-status]');
  const result = card.querySelector('[data-frequency-result]');
  const volume = card.querySelector('[data-frequency-volume]');
  const volumeLevel = () => Math.min(100,Math.max(0,Number(volume.value)||0));
  const volumeGain = () => .04 * volumeLevel()/100;
  const LOW = 500, HIGH = 20000, DURATION = 24;
  let context, oscillator, gain, volumeNode, startedAt = 0, generation = 0, timer, interval;
  let transition = Promise.resolve();
  const progress = () => context ? Math.min(1, Math.max(0,(context.currentTime-startedAt)/DURATION)) : 0;
  const hzAt = fraction => LOW * Math.pow(HIGH/LOW,fraction);
  // Original fictional game curve, not population or clinical hearing-age data.
  // Interpolate each integer age from 90 to 18, with more resolution at high notes.
  const ageStops = [[500,90],[8000,72],[12000,58],[14000,46],[16000,34],[18000,24],[20000,18]];
  const hearingAge = fraction => {
    const hz=hzAt(Math.min(1,Math.max(0,fraction)));
    for(let i=1;i<ageStops.length;i++){
      const [upper,young]=ageStops[i], [lower,old]=ageStops[i-1];
      if(hz<=upper)return Math.round(old+(young-old)*(hz-lower)/(upper-lower));
    }
    return 18;
  };
  const trailRank = age => {
    if(age<=22)return ['Campfire rookie','Fresh boots. Big plans.'];
    if(age<=29)return ['Dawn patrol','First coffee. First light.'];
    if(age<=39)return ['Trail scout','Always wondering what’s over the next ridge.'];
    if(age<=49)return ['Backcountry regular','Your favorite route takes the long way home.'];
    if(age<=59)return ['Seasoned tracker','A good map and a better story.'];
    if(age<=69)return ['Camp captain','You know where the good camp spots are.'];
    if(age<=79)return ['Old-school outdoors','Flannel, a thermos and no rush to head home.'];
    return ['Campfire legend','Save the best seat. You’ve got stories to tell.'];
  };
  const label = hz => Math.round(hz).toLocaleString('en-US');
  const paint = fraction => {
    card.querySelector('[data-frequency-value]').textContent = label(hzAt(fraction));
    card.querySelector('[data-frequency-arc]').style.strokeDasharray = String(fraction*100)+' 100';
  };
  const silence = () => {
    generation++;clearTimeout(timer);clearInterval(interval);
    if(gain){try{gain.gain.cancelScheduledValues(context.currentTime);gain.gain.setValueAtTime(0,context.currentTime);gain.disconnect();}catch(_){}gain=null;}
    if(volumeNode){try{volumeNode.disconnect();}catch(_){}volumeNode=null;}
    if(oscillator){try{oscillator.stop();oscillator.disconnect();}catch(_){}oscillator=null;}
    const oldContext=context;
    if(oldContext)transition=transition.catch(()=>{}).then(()=>oldContext.suspend()).catch(()=>{});
    start.disabled=false;stop.disabled=true;
  };
  const finish = reason => {
    const wasRunning=card.dataset.state==='running';
    const fraction=wasRunning?(reason==='complete'?1:progress()):0;
    silence();
    card.querySelector('[data-frequency-start-label]').textContent='Try again';
    if(!wasRunning || reason==='hidden' || reason==='error'){
      card.dataset.state='ready';result.hidden=true;paint(0);
      card.querySelector('[data-frequency-label]').textContent='READY WHEN YOU ARE';
      card.querySelector('[data-frequency-caption]').textContent='The pitch rises. You choose where it stops.';
      status.textContent=reason==='hidden'?'Sound stopped because you left this page.':reason==='error'?'Audio couldn’t start. Try another browser or check your audio output.':'Sweep cancelled before sound started.';
      return;
    }
    card.dataset.state='result';paint(fraction);
    card.querySelector('[data-frequency-label]').textContent=reason==='complete'?'SWEEP COMPLETE':'YOUR STOPPING POINT';
    const age=hearingAge(fraction);
    const [rank,flavor]=trailRank(age);
    card.querySelector('[data-frequency-caption]').textContent='Your hearing age is ready below.';
    card.querySelector('[data-frequency-result-heading]').textContent=age+' years';
    card.querySelector('[data-frequency-result-rank]').textContent=rank;
    card.querySelector('[data-frequency-result-flavor]').textContent=flavor;
    card.querySelector('[data-frequency-result-copy]').textContent=reason==='complete'?'You made it to 20,000 Hz — the top of the trail.':'Your stopping point: '+label(hzAt(fraction))+' Hz.';
    result.hidden=false;
    status.textContent='Sound is off. Your hearing age game score is '+age+' years.';
  };
  start.addEventListener('click',async()=>{
    if(card.dataset.state==='running'||card.dataset.state==='starting')return;
    silence();const token=generation;
    card.dataset.state='starting';result.hidden=true;paint(0);start.disabled=true;stop.disabled=false;
    card.querySelector('[data-frequency-label]').textContent='GETTING READY';status.textContent='Starting quietly. Keep your device volume low.';
    try{
      const Audio=window.AudioContext||window.webkitAudioContext;if(!Audio)throw new Error('Audio unavailable');
      if(!context){context=new Audio();context.onstatechange=()=>{if(context.state!=='running'&&card.dataset.state==='running')finish('error');};}
      if(context.sampleRate/2<=HIGH)throw new Error('Sample rate too low');
      const resume=transition.catch(()=>{}).then(()=>token===generation&&!document.hidden?context.resume():undefined);
      transition=resume;await resume;
      if(token!==generation||document.hidden)return;
      if(context.state!=='running')throw new Error('Audio did not start');
      oscillator=context.createOscillator();gain=context.createGain();volumeNode=context.createGain();startedAt=context.currentTime;
      oscillator.type='sine';oscillator.frequency.setValueAtTime(LOW,startedAt);
      oscillator.frequency.exponentialRampToValueAtTime(HIGH,startedAt+DURATION);
      // Separate envelope and volume so slider changes preserve the scheduled fade-out.
      // Default 30% of .04 retains the previous .012 digital gain; no SPL calibration.
      gain.gain.setValueAtTime(0,startedAt);gain.gain.linearRampToValueAtTime(1,startedAt+.05);
      gain.gain.setValueAtTime(1,startedAt+DURATION-.05);gain.gain.linearRampToValueAtTime(0,startedAt+DURATION);
      volumeNode.gain.setValueAtTime(volumeGain(),startedAt);
      oscillator.connect(gain);gain.connect(volumeNode);volumeNode.connect(context.destination);
      card.dataset.state='running';card.querySelector('[data-frequency-label]').textContent='FOLLOW THE RISING TONE';
      card.querySelector('[data-frequency-caption]').textContent='Press Stop when the tone is no longer audible.';
      status.textContent='Keep volume low. Press Stop when you can’t hear it, or if uncomfortable.';
      oscillator.onended=()=>{if(token===generation&&card.dataset.state==='running')finish('complete');};
      oscillator.start(startedAt);oscillator.stop(startedAt+DURATION);
      interval=setInterval(()=>{if(token===generation)paint(progress());},80);
      timer=setTimeout(()=>{if(token===generation)finish('complete');},(DURATION+1)*1000);
    }catch(_){if(token===generation)finish('error');}
  });
  stop.addEventListener('click',()=>finish('stop'));
  volume.addEventListener('input',()=>{
    const level=volumeLevel();
    card.querySelector('[data-frequency-volume-value]').textContent=level===0?'Muted':level+'%';
    volume.setAttribute('aria-valuetext',level===0?'Muted':level+' percent');
    if(volumeNode&&context){
      const now=context.currentTime;
      volumeNode.gain.cancelScheduledValues(now);
      if(level===0)volumeNode.gain.setValueAtTime(0,now);
      else volumeNode.gain.setTargetAtTime(volumeGain(),now,.015);
    }
  });
  document.addEventListener('visibilitychange',()=>{if(document.hidden&&(card.dataset.state==='running'||card.dataset.state==='starting'))finish('hidden');});
  window.addEventListener('pagehide',()=>{if(card.dataset.state==='running'||card.dataset.state==='starting')finish('hidden');});
})();
